/**
 * Browser-local persistence for Screener saved screens and the MyWatch list.
 * No backend job consumes these screens, so localStorage is the canonical
 * store (FILTER_FUNCTIONAL_SPEC.md §6). Writes throw on storage failure so
 * the UI never announces success for an unsaved change.
 */
import { SCHEMA_VERSION, validateSavedState, parseTickers } from './screenerFilters';

const MAX_NAME_LENGTH = 80;

export function savedScreensKey(userId) {
  return `screener_saved_filters_${userId || 'anonymous'}`;
}

export function watchlistKey(userId) {
  return `screener_watchlist_${userId || 'anonymous'}`;
}

function readJson(storage, key) {
  const raw = storage.getItem(key);
  if (!raw) return null;
  return JSON.parse(raw);
}

/** Returns { filters: [...], error } — a corrupt store is reported, not hidden. */
export function loadSavedScreens(userId, storage = window.localStorage) {
  try {
    const parsed = readJson(storage, savedScreensKey(userId));
    if (!parsed) return { filters: [], error: null };
    if (!Array.isArray(parsed.filters)) {
      return { filters: [], error: 'Saved screens could not be read (unexpected format).' };
    }
    const filters = parsed.filters.filter((f) => f && typeof f.name === 'string' && f.state);
    return { filters, error: null };
  } catch (error) {
    return { filters: [], error: `Saved screens could not be read: ${error.message}` };
  }
}

function writeScreens(userId, filters, storage) {
  storage.setItem(savedScreensKey(userId), JSON.stringify({ schemaVersion: SCHEMA_VERSION, filters }));
}

export function normalizeScreenName(name) {
  return String(name || '').trim();
}

/**
 * Save a complete snapshot under `name`, overwriting a same-named screen.
 * Returns the new list. Throws on invalid input or storage failure.
 */
export function saveScreen(userId, existing, name, state, storage = window.localStorage) {
  const trimmed = normalizeScreenName(name);
  if (!trimmed) throw new Error('Enter a name for this screen.');
  if (trimmed.length > MAX_NAME_LENGTH) throw new Error(`Screen names are limited to ${MAX_NAME_LENGTH} characters.`);
  const { state: snapshot, errors } = validateSavedState(state);
  if (errors.length) throw new Error(errors.join(' '));

  const record = { name: trimmed, email_enabled: false, state: snapshot };
  const index = existing.findIndex((f) => f.name === trimmed);
  const next = index >= 0
    ? existing.map((f, i) => (i === index ? record : f))
    : [...existing, record];
  writeScreens(userId, next, storage);
  return next;
}

export function deleteScreen(userId, existing, name, storage = window.localStorage) {
  const next = existing.filter((f) => f.name !== name);
  writeScreens(userId, next, storage);
  return next;
}

export function loadWatchlist(userId, storage = window.localStorage) {
  try {
    const parsed = readJson(storage, watchlistKey(userId));
    return Array.isArray(parsed) ? parseTickers(parsed.join(',')) : [];
  } catch (error) {
    return [];
  }
}

export function saveWatchlist(userId, text, storage = window.localStorage) {
  const tickers = parseTickers(text);
  storage.setItem(watchlistKey(userId), JSON.stringify(tickers));
  return tickers;
}
